/**
 * Settings' words. Each is a draft for the author unless said here. The world zoom's label is the brief's name for
 * the setting; Farther, Default and Closer are its choices. The two Nameplates labels, each its choice's accessible
 * name too, are drafts (P1.9-T24), and their choices' words are the row's. Show health numbers is the author's name
 * for the setting (2026-09-27). Reduced motion is the design's name for its Accessibility entry, which R-0061 asks
 * for, and its choices are the design's; System follows the device. Show performance is a draft (ADR-087). Render
 * resolution is ADR-093's name for the setting. Frame limit is the design's name for its row (`SettingsSurfaces.jsx`)
 * and its choices are the design's "Match display" and "60 fps" with "30 fps" and "120 fps" beside them (ADR-093's
 * amendment of 2026-10-13); its two notes are drafts. Auto loot (P2.8-T79) is the author's words. The section names, the
 * section list's accessible name, the changed mark's, Reset this tab, Cancel, Apply and Discard, and Reset's confirm
 * are the design's (`SettingsSurfaces.jsx`; P1.9-T44), as is the count of changed rows ("1 change", "2 changes").
 * Audio's words are Master's label, a level's Mute, the background choice, which is the design's (P1.9-T49), and a
 * level's slider's accessible name, the level's label and "volume". `audio.channels` holds each mixer channel's name
 * by its id (the shared `Channel` values), which a level's label reads; "Off" is a muted slider's value.
 *
 * `changes`, `unsaved` and `zoomSame` choose their form by `count`; `zoomSame` does not print it. `zoomBoth` joins
 * two choices' names for `zoomSame`'s `{choices}`.
 */
export const SETTINGS = {
  title: "Settings",
  renderResolution: "Render resolution",
  frameLimit: "Frame limit",
  frameLimitChoices: { fps30: "30 fps", fps60: "60 fps", fps120: "120 fps", match: "Match display" },
  frameLimitNotes: {
    thirty: "At 30, a key press can take up to 33 ms longer to be sent.",
    thirtyTouch: "At 30, a tap can take up to 33 ms longer to be sent.",
    slow: "This screen refreshes at {hz} Hz, so 120 would do nothing.",
    unmeasured: "The 120 choice is offered once the refresh rate of this screen has been measured.",
  },
  zoom: "World zoom",
  othersNameplates: "Other players' nameplates",
  ownNameplate: "Your nameplate",
  healthNumbers: "Show health numbers",
  idleMarks: "Show idle and AFK marks",
  emotesOthers: "Show other players' emotes",
  coordinates: "Show coordinates",
  compassPlaces: "Places on the compass",
  autoLoot: "Auto loot",
  barKeys: "Show key numbers on the bar",
  barPaging: "Ability bar",
  barPagings: { all: "All 10", five: "Five at a time" },
  partyInvites: "Party invitations",
  partyInviteChoices: { map: "On my map", nobody: "Nobody" },
  barPagingNote: "Where ten do not fit, the bar shows five either way.",
  chatSize: "Chat size",
  chatSizes: { small: "Small", medium: "Medium", large: "Large" },
  resetWindows: "Reset windows",
  windowsReset: "Windows reset",
  reducedMotion: "Reduced motion",
  interfaceSize: "Interface size",
  interfaceSizes: { smallest: "Smallest", smaller: "Smaller", auto: "Auto", larger: "Larger", largest: "Largest" },
  interfaceSizeNote: "This window holds up to {most}x. Auto is {auto}x.",
  chatText: "Chat text size",
  chatTexts: { normal: "Normal", large: "Large", larger: "Larger", largest: "Largest" },
  performance: "Show performance",
  audio: {
    master: "Master",
    mute: "Mute",
    background: "Mute when the window is in the background",
    whispers: "Whispers alert in the background",
    volume: "{level} volume",
    off: "Off",
    channels: { music: "Music", stingers: "Stingers", player: "Player", others: "Other players", mobs: "Mobs", npc: "NPC", world: "World", ambience: "Ambience", ui: "UI" },
  },
  sections: {
    display: "Display",
    interface: "Interface",
    audio: "Audio",
    controls: "Controls",
    access: "Accessibility",
    social: "Social",
    account: "Account",
  },
  sectionList: "Sections",
  changed: "Changed",
  reset: "Reset this tab",
  cancel: "Cancel",
  apply: "Apply",
  discard: "Discard",
  unsavedTitle: "Apply your changes?",
  resetConfirm: "Reset",
  changes: { one: "{count} change", other: "{count} changes" },
  unsaved: { one: "You changed {count} setting.", other: "You changed {count} settings." },
  resetQuestion: {
    title: "Reset {section}?",
    body: "Every {section} setting returns to its default.",
  },
  motionChoices: { system: "System", on: "On", off: "Off" },
  othersNameplateChoices: { first: "First name", full: "Full name", flag: "Flag only", off: "Off" },
  ownNameplateChoices: { off: "Off", on: "On" },
  zoomChoices: { farther: "Farther", default: "Default", closer: "Closer" },
  zoomBoth: "{first} and {second}",
  zoomSame: {
    one: "{choices} is the same as Default at this render resolution.",
    other: "{choices} are the same as Default at this render resolution.",
  },
} as const;
